<template>
  <div class="pa-2 pb-10">
    <v-breadcrumbs :items="breadcrumbs" class="breadcrumbs" />
    <heading>{{ $t("betriebsanweisung.templatesTitle") }}</heading>

    <v-card outlined>
      <v-card-title class="d-flex align-center">
        <v-text-field
          v-model="search"
          single-line
          hide-details
          dense
          clearable
          :label="$t('general.search')"
          style="max-width: 370px"
        />
        <v-spacer />
        <v-btn
          v-if="canManageStructureHere"
          color="secondary"
          outlined
          @click="openCreateFolder"
        >
          {{ $t("betriebsanweisung.folder.new") }}
          <v-icon right>mdi-folder-plus</v-icon>
        </v-btn>
      </v-card-title>

      <v-data-table
        :headers="headers"
        :items="rows"
        :search="search"
        :loading="loading"
        item-key="_id"
        class="elevation-0 pointer-rows"
        :no-data-text="$t('general.noData')"
        :items-per-page="-1"
        :footer-props="{ itemsPerPageOptions: [-1, 10, 25, 50] }"
        @click:row="openFolder"
      >
        <template v-slot:item.name="{ item }">
          <v-icon left color="primary">
            {{ item.isNone ? "mdi-folder-outline" : "mdi-folder" }}
          </v-icon>
          {{ item.name }}
        </template>

        <template v-slot:item.type="{ item }">
          <v-chip
            v-if="!item.isNone"
            x-small
            label
            :color="item.type === 'global' ? 'primary' : 'secondary'"
            dark
          >
            {{
              item.type === "global"
                ? $t("betriebsanweisung.global")
                : $t("betriebsanweisung.local")
            }}
          </v-chip>
        </template>

        <template v-slot:item.count="{ item }">
          {{ item.count }}
        </template>

        <template v-slot:item.actions="{ item }">
          <template v-if="!item.isNone">
            <v-btn
              v-if="canEditStructureHere"
              icon
              @click.stop="openRenameFolder(item)"
              :disabled="!canEdit(item)"
            >
              <v-icon>mdi-pencil</v-icon>
            </v-btn>
            <v-btn
              v-if="canManageStructureHere"
              icon
              @click.stop="confirmDeleteFolder(item)"
              :disabled="!canEdit(item)"
            >
              <v-icon>mdi-delete</v-icon>
            </v-btn>
          </template>
        </template>
      </v-data-table>
    </v-card>

    <v-dialog v-model="folderDialog.open" max-width="480">
      <v-card>
        <v-card-title>
          {{
            folderDialog.mode === "edit"
              ? $t("betriebsanweisung.folder.rename")
              : $t("betriebsanweisung.folder.new")
          }}
        </v-card-title>
        <v-card-text>
          <v-text-field
            v-model="folderDialog.name"
            :label="$t('betriebsanweisung.folder.nameLabel')"
            autofocus
          />
          <v-select
            v-if="isMaster && folderDialog.mode === 'new'"
            v-model="folderDialog.type"
            :items="typeOptions"
            :label="$t('betriebsanweisung.scope')"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="folderDialog.open = false">
            {{ $t("general.back") }}
          </v-btn>
          <v-btn
            color="primary"
            text
            :disabled="!folderDialog.name || !folderDialog.name.trim()"
            @click="saveFolder"
          >
            {{ $t("general.save") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="deleteDialog.open" max-width="440">
      <v-card>
        <v-card-title>
          {{ $t("betriebsanweisung.folder.confirmDelete") }}
        </v-card-title>
        <v-card-text>
          {{
            $t("betriebsanweisung.folder.confirmDeleteText", {
              name: deleteDialog.item ? deleteDialog.item.name : "",
            })
          }}
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="deleteDialog.open = false">
            {{ $t("general.back") }}
          </v-btn>
          <v-btn color="error" text @click="deleteFolder">
            {{ $t("general.delete") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { isMaster } from "../../utils/masterEmails";
import i18n from "../../i18n";

export default {
  name: "betriebsanweisungTemplates",
  data() {
    return {
      folders: [],
      counts: {},
      uncategorizedCount: 0,
      loading: false,
      search: "",
      isMaster: false,
      folderDialog: {
        open: false,
        mode: "new",
        id: null,
        name: "",
        type: "local",
      },
      deleteDialog: { open: false, item: null },
    };
  },
  computed: {
    breadcrumbs() {
      return [
        {
          text: i18n.t("sidebar.operatingInstructions"),
          disabled: false,
          href: "/app/instructionCategory",
        },
        { text: i18n.t("betriebsanweisung.templatesTitle"), disabled: true },
      ];
    },
    typeOptions() {
      return [
        { text: this.$t("betriebsanweisung.local"), value: "local" },
        { text: this.$t("betriebsanweisung.global"), value: "global" },
      ];
    },
    headers() {
      return [
        {
          text: this.$t("betriebsanweisung.folder.nameLabel"),
          value: "name",
          align: "left",
        },
        { text: this.$t("betriebsanweisung.scope"), value: "type" },
        {
          text: this.$t("folderCount.total"),
          value: "count",
          align: "center",
          sortable: false,
        },
        { text: "", value: "actions", align: "right", sortable: false },
      ];
    },
    rows() {
      const folderRows = this.folders.map((f) => ({
        ...f,
        count: (this.counts[f._id] && this.counts[f._id].total) || 0,
        isNone: false,
      }));
      folderRows.push({
        _id: "none",
        name: this.$t("betriebsanweisung.folder.ohneOrdner"),
        type: null,
        count: this.uncategorizedCount,
        isNone: true,
      });
      return folderRows;
    },
  },
  async created() {
    const user = await this.$store.dispatch("getUserInfo");
    this.isMaster = user && isMaster(user.email);
    this.loadFolders();
  },
  methods: {
    async loadFolders() {
      this.loading = true;
      try {
        const [foldersRes, counts, uncategorized] = await Promise.all([
          this.$store.dispatch("getBetriebsanweisungTemplateCategories"),
          this.$store.dispatch("getBetriebsanweisungTemplateCategoryCounts"),
          this.$store.dispatch("getBetriebsanweisungTemplates", {
            category: "none",
          }),
        ]);
        this.folders = (foldersRes && foldersRes.data) || [];
        this.counts = counts || {};
        this.uncategorizedCount =
          ((uncategorized && uncategorized.data) || []).length || 0;
      } finally {
        this.loading = false;
      }
    },
    canEdit(folder) {
      if (this.isMaster) return true;
      return folder.type === "local";
    },
    openFolder(item) {
      this.$router.push({
        name: "betriebsanweisungTemplateList",
        params: {
          templateCategoryId: item._id,
          templateCategoryName: item.name,
        },
        query: this.$route.query,
      });
    },
    openCreateFolder() {
      this.folderDialog = {
        open: true,
        mode: "new",
        id: null,
        name: "",
        type: "local",
      };
    },
    openRenameFolder(folder) {
      this.folderDialog = {
        open: true,
        mode: "edit",
        id: folder._id,
        name: folder.name,
        type: folder.type,
      };
    },
    async saveFolder() {
      const name = (this.folderDialog.name || "").trim();
      if (!name) return;
      if (this.folderDialog.mode === "edit") {
        await this.$store.dispatch("updateBetriebsanweisungTemplateCategory", {
          id: this.folderDialog.id,
          name,
        });
      } else {
        await this.$store.dispatch("createBetriebsanweisungTemplateCategory", {
          name,
          type: this.isMaster ? this.folderDialog.type : "local",
        });
      }
      this.folderDialog.open = false;
      this.loadFolders();
    },
    confirmDeleteFolder(folder) {
      this.deleteDialog.item = folder;
      this.deleteDialog.open = true;
    },
    async deleteFolder() {
      if (!this.deleteDialog.item) return;
      await this.$store.dispatch(
        "deleteBetriebsanweisungTemplateCategory",
        this.deleteDialog.item._id
      );
      this.deleteDialog.open = false;
      this.deleteDialog.item = null;
      this.loadFolders();
    },
  },
};
</script>

<style scoped>
.pointer-rows >>> tbody tr {
  cursor: pointer;
}
</style>
